import React, { FC } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import config from "../../config";
import { formatFilmDay, formatFilmDayList } from "../../utils/filmWindow";

interface Props {
  filmDate?: string | null;
  filmDays?: string[];
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  locked?: boolean;
}

const toggleSx = {
  fontFamily: "CircularStd",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  px: 1.5,
  py: 0.5,
  color: "#6B6B6B",
  "&.Mui-selected": {
    backgroundColor: "#A58450",
    color: "#fff",
    "&:hover": { backgroundColor: "#80582A" },
  },
  "&.Mui-disabled.Mui-selected": {
    backgroundColor: "#A58450",
    color: "#fff",
    opacity: 0.8,
  },
};

export const FilmDateConfirmField: FC<Props> = ({
  filmDate,
  filmDays,
  value,
  onChange,
  locked = false,
}) => {
  const days = filmDays && filmDays.length > 0 ? filmDays : [];
  if (!filmDate && days.length === 0) return null;

  const multiDay = days.length > 1;
  const singleDay = days.length === 1 ? days[0] : filmDate;
  const dayLabel = multiDay
    ? formatFilmDayList(days)
    : formatFilmDay(singleDay);

  return (
    <Box className="mb-6 flex items-start gap-3 rounded-md bg-primaryLight px-4 py-3">
      <EventAvailableOutlinedIcon
        sx={{ fontSize: 18, color: "#A58450", mt: "2px", flexShrink: 0 }}
      />
      <Box className="flex flex-col gap-1 min-w-0">
        <Typography className="!font-circular !text-[13px] !font-semibold !leading-tight !text-primary">
          {multiDay ? "Confirm the filming dates" : "Confirm the filming date"}
        </Typography>
        <Typography className="!font-circular !text-[13px] !font-[450] !leading-[1.6] !text-dark1">
          {multiDay
            ? `This is a ${days.length} day shoot (${dayLabel}). You need to be available on every one of these days.`
            : `Filming is scheduled for ${dayLabel}.`}
        </Typography>
        {/* Explicit yes/no with a real neutral: nothing preselected, and
            re-clicking the selected option returns to unanswered — an
            accidental tap can be fully undone. Neutral submits no
            user_confirmed at all, so it never reads as a decline. */}
        <ToggleButtonGroup
          exclusive
          size="small"
          disabled={locked}
          value={value === null ? null : value ? "yes" : "no"}
          onChange={(_e, next: "yes" | "no" | null) =>
            onChange(next === null ? null : next === "yes")
          }
          sx={{ mt: 0.5, alignSelf: "flex-start" }}
        >
          <ToggleButton value="yes" sx={toggleSx}>
            I&apos;m available
          </ToggleButton>
          <ToggleButton value="no" sx={toggleSx}>
            I&apos;m not available
          </ToggleButton>
        </ToggleButtonGroup>
        {locked && (
          <Typography
            sx={{
              fontFamily: "CircularStd",
              fontSize: "12px",
              color: "#6B6B6B",
              mt: 0.25,
            }}
          >
            Need to reschedule? Contact{" "}
            <Box
              component="a"
              href={`mailto:${config.CONTACT_US_EMAIL}`}
              rel="noreferrer"
              target="_blank"
              sx={{
                color: "#80582A",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              {config.CONTACT_US_EMAIL}
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
